import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { DuplicateQueryModal } from '@/sections/insights/queryBuilder/components/Modals';
import { QueryResults } from '@/sections/insights/queryBuilder/components/QueryResults';
import { useQueryOperations } from '@/sections/insights/queryBuilder/hooks/useQueryOperations';
import { useQueryBuilder } from '@/sections/insights/queryBuilder/state/useQueryBuilder';
import type { SavedQuery } from '@/sections/insights/queryBuilder/types/SavedQuery';

import { EmptyState } from './EmptyState';
import { QuerySplitLayout } from './QuerySplitLayout';
import { QuerySimpleVisualizer } from './QuerySplitVisualizer';
import { ActionsToolbar } from './QuerySplitVisualizer/ActionsToolbar';

/**
 * QuerySplitViewer - Read-only query interface with split-pane layout
 *
 * Layout:
 * - Left pane: QuerySimpleVisualizer (read-only)
 * - Right pane: EmptyState initially, QueryResults after execution
 * - No action panel (as per design requirements)
 *
 * Behavior:
 * - Same as QuerySplitEditor but read-only
 * - Matches QueryWriter pattern: EmptyState → QueryResults
 * - Storage key: "querySplitViewerRatio"
 *
 * Triggered by:
 * - QueryMenu "View Query (Split View)" action
 * - QueryBuilderWorkstation when mode === 'split-view'
 */
export const QuerySplitViewer = () => {
  const { queryState, isGlobal, getSavedQuery } = useQueryBuilder();
  const [showResults, setShowResults] = useState(false);
  const [executedQuery, setExecutedQuery] = useState<SavedQuery | null>(null);

  // Add action handlers (NOTE: No handleSave for viewer)
  const { isSaving, getExecutableQuery } = useQueryOperations();
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);

  const handleDuplicate = () => {
    setIsDuplicateModalOpen(true);
  };

  const handleExecute = () => {
    const currentQuery = getExecutableQuery();
    if (!currentQuery) {
      return;
    }

    setExecutedQuery(currentQuery);
    setShowResults(true);
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Valid use case: resetting UI state when query changes
    setExecutedQuery(null);
    setShowResults(false);
  }, [queryState?.metadata.id]);

  // Error handling for missing query
  if (!queryState) {
    toast.error('No query selected');
    return null;
  }

  return (
    <>
      <QuerySplitLayout
        leftContent={
          <>
            <ActionsToolbar
              editable={false}
              isGlobal={isGlobal}
              onExecute={handleExecute}
              onDuplicate={handleDuplicate}
              isSaving={isSaving}
              // NO onSave prop - viewer is read-only
            />

            <div className="relative h-full">
              <QuerySimpleVisualizer editable={false} />
            </div>
          </>
        }
        rightContent={
          showResults ? (
            <QueryResults selectedQuery={executedQuery} />
          ) : (
            <EmptyState />
          )
        }
        storageKey="querySplitViewerRatio"
        testId="query-split-viewer"
      />

      {/* Duplicate Modal */}
      {getSavedQuery() && (
        <DuplicateQueryModal
          isOpen={isDuplicateModalOpen}
          onClose={() => setIsDuplicateModalOpen(false)}
          originalQuery={getSavedQuery()!}
        />
      )}
    </>
  );
};
